'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { isLocalPathIntegration } from '@terra/shared-contracts-git';
import { GitIntegrationPlatformSchema } from '@terra/shared-schemas-git';
import { VStack } from '@terra/ui-styled-system';
import {
  ApplicationSourceCodeSection,
  Button,
  DisconnectSourceCodeDialog,
  MetaDefault,
  SourceCodeIntegrationCard,
  toasterApi,
} from '@terra/ui-design-system';
import {
  useApplicationSourceCodeIntegrations,
  useDisconnectApplicationSourceCode,
} from '../../../hooks/api/useApplicationSourceCodeIntegrations';
import { useOpenWebAppPage } from '../../../hooks/useOpenWebAppPage';

type TargetSourceCodePanelProps = {
  applicationId: string;
  webSettingsPath: string;
  isReadOnly: boolean;
};

export function TargetSourceCodePanel({
  applicationId,
  webSettingsPath,
  isReadOnly,
}: TargetSourceCodePanelProps) {
  const t = useTranslations('designSystem.applicationSettings.sourceCode');
  const openWebAppPage = useOpenWebAppPage();
  const { items, isLoading, isFetching } = useApplicationSourceCodeIntegrations(
    { applicationId },
  );
  const disconnect = useDisconnectApplicationSourceCode({ applicationId });
  const [disconnectingIntegrationId, setDisconnectingIntegrationId] = useState<
    string | null
  >(null);
  const openWebSettings = () => openWebAppPage(webSettingsPath);

  const handleDisconnect = async () => {
    if (!disconnectingIntegrationId) {
      return;
    }

    try {
      await disconnect.mutateAsync(disconnectingIntegrationId);
      setDisconnectingIntegrationId(null);
      toasterApi.success({
        title: t('disconnectSuccessTitle'),
        description: t('disconnectSuccessDescription'),
      });
    } catch (error) {
      console.error(
        new Error('Failed to disconnect source code integration', {
          cause: error instanceof Error ? error : new Error(String(error)),
        }),
      );
      toasterApi.error({
        title: t('disconnectErrorTitle'),
        description:
          error instanceof Error && error.message
            ? error.message
            : t('disconnectErrorDescription'),
      });
    }
  };

  const addSourceCodeButton = (
    <Button variant="ghost" size="xs" onClick={openWebSettings}>
      {t('addSourceCode')}
    </Button>
  );
  const hasIntegrations = items.length > 0;
  const emptyContent = isReadOnly ? undefined : (
    <VStack gap={3} alignItems="center" py={2}>
      <MetaDefault color="fg.muted">{t('noIntegrations')}</MetaDefault>
      {addSourceCodeButton}
    </VStack>
  );

  return (
    <>
      <ApplicationSourceCodeSection
        isLoading={isLoading || (isFetching && !hasIntegrations)}
        isEmpty={!hasIntegrations}
        action={
          hasIntegrations && !isReadOnly ? addSourceCodeButton : undefined
        }
        emptyContent={emptyContent}
      >
        {items.map(({ appIntegration, details, isDetailsLoading, isValid }) =>
          details ? (
            <SourceCodeIntegrationCard
              key={appIntegration.id}
              integration={details}
              isMounted={isLocalPathIntegration({
                platform: details.platform,
                name: details.name ?? '',
              })}
              connectedAt={appIntegration.createdAt}
              repositories={appIntegration.repositories}
              pollingIntervalHours={appIntegration.pollingIntervalHours}
              isValidIntegration={isValid}
              isLoading={isDetailsLoading}
              isReadOnly={isReadOnly}
              onEdit={openWebSettings}
              onAddRepositories={openWebSettings}
              onTransform={
                appIntegration.platform ===
                GitIntegrationPlatformSchema.enum.ZIP_UPLOAD
                  ? openWebSettings
                  : undefined
              }
              onDisconnect={() =>
                setDisconnectingIntegrationId(appIntegration.id)
              }
            />
          ) : null,
        )}
      </ApplicationSourceCodeSection>
      <DisconnectSourceCodeDialog
        isOpen={Boolean(disconnectingIntegrationId)}
        isLoading={disconnect.isPending}
        onOpenChange={(open) => {
          if (!open) {
            setDisconnectingIntegrationId(null);
          }
        }}
        onConfirm={handleDisconnect}
      />
    </>
  );
}
